/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface UnitNoteProps {
  id: string;
  content: string;
  noteTime: Date;
  authorId: string | null;
  noteCategory: string | null;
  noteMetadataJson: string | null;
  unitId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class UnitNoteRecord extends Record<UnitNoteProps>({
  id: null as any,
  content: null as any,
  noteTime: null as any,
  authorId: null as any,
  noteCategory: null as any,
  noteMetadataJson: null as any,
  unitId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    content: string;
    noteTime: string | number | Date;
    authorId: string | null;
    noteCategory: string | null;
    noteMetadataJson: string | null;
    unitId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): UnitNoteRecord {
    if (json == null) {
      throw new Error('UnitNoteRecord.fromJS: parameter cannot be null');
    }
    return new UnitNoteRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'UnitNoteJson.id',
      ),
      content: getFromOrThrow(json, (j) => j.content, 'UnitNoteJson.content'),
      noteTime: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'noteTime', '0001-01-01T00:00:00.00Z'),
          'UnitNoteJson.noteTime',
        ),
      ),
      authorId: json.authorId,
      noteCategory: json.noteCategory,
      noteMetadataJson: json.noteMetadataJson,
      unitId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'unitId', '00000000-0000-0000-0000-000000000000'),
        'UnitNoteJson.unitId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'UnitNoteJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    content: string;
    noteTime: string | number | Date;
    authorId: string | null;
    noteCategory: string | null;
    noteMetadataJson: string | null;
    unitId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [UnitNoteRecord, Error[]] {
    if (json == null) {
      throw new Error('UnitNoteRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new UnitNoteRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'UnitNoteJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      content: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.content, 'UnitNoteJson.content'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      noteTime: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'noteTime', '0001-01-01T00:00:00.00Z'),
              'UnitNoteJson.noteTime',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      authorId: tryOrDefault(
        () => json.authorId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      noteCategory: tryOrDefault(
        () => json.noteCategory,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      noteMetadataJson: tryOrDefault(
        () => json.noteMetadataJson,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'unitId', '00000000-0000-0000-0000-000000000000'),
            'UnitNoteJson.unitId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'UnitNoteJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: UnitNoteProps) {
    super(props);
    if (props == null) {
      throw new Error('UnitNoteRecord constructor parameter cannot be null');
    }
  }
}

export abstract class UnitNoteViewModelBase implements UnitNoteProps {
  protected readonly _props: UnitNoteProps;
  private _json?: UnitNoteProps;

  get id(): string {
    return this._props.id;
  }
  get content(): string {
    return this._props.content;
  }
  get noteTime(): Date {
    return this._props.noteTime;
  }
  get authorId(): string | null {
    return this._props.authorId;
  }
  get noteCategory(): string | null {
    return this._props.noteCategory;
  }
  get noteMetadataJson(): string | null {
    return this._props.noteMetadataJson;
  }
  get unitId(): string {
    return this._props.unitId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: UnitNoteProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        content: this._props.content,
        noteTime: this._props.noteTime,
        authorId: this._props.authorId,
        noteCategory: this._props.noteCategory,
        noteMetadataJson: this._props.noteMetadataJson,
        unitId: this._props.unitId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllUnitNoteEntities = (state: AppState) => state.entityStores.unitNote.entities;

export const selectUnitNoteById = (id: string) => (state: AppState) =>
  state.entityStores.unitNote.entities.get(id);

export const selectUnitNoteMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.unitNote.meta.get(id);

export const selectUnitNoteAndMetadataById = (id: string) =>
  createSelector(selectUnitNoteById(id), selectUnitNoteMetadataById(id), (unitNote, meta) => ({
    unitNote,
    meta,
  }));

export function selectUnitNoteEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllUnitNoteEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, UnitNoteRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectUnitNoteEntitiesByContent(content: string) {
  return createSelector(selectAllUnitNoteEntities, (entities) =>
    entities.filter((e) => e.content === content).valueSeq(),
  );
}

export function selectUnitNoteEntitiesByNoteTime(noteTime: Date) {
  return createSelector(selectAllUnitNoteEntities, (entities) =>
    entities.filter((e) => e.noteTime === noteTime).valueSeq(),
  );
}

export function selectUnitNoteEntitiesByAuthorId(authorId: string) {
  return createSelector(selectAllUnitNoteEntities, (entities) =>
    entities.filter((e) => e.authorId === authorId).valueSeq(),
  );
}

export function selectUnitNoteEntitiesByNoteCategory(noteCategory: string) {
  return createSelector(selectAllUnitNoteEntities, (entities) =>
    entities.filter((e) => e.noteCategory === noteCategory).valueSeq(),
  );
}

export function selectUnitNoteEntitiesByNoteMetadataJson(noteMetadataJson: string) {
  return createSelector(selectAllUnitNoteEntities, (entities) =>
    entities.filter((e) => e.noteMetadataJson === noteMetadataJson).valueSeq(),
  );
}

export function selectUnitNoteEntitiesByUnitId(unitId: string) {
  return createSelector(selectAllUnitNoteEntities, (entities) =>
    entities.filter((e) => e.unitId === unitId).valueSeq(),
  );
}

export function selectUnitNoteEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllUnitNoteEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
